/**
 * @fileoverview Active Clients grid query hook.
 * @description Wraps GET /sales/clients in a TanStack query, one page at a time. The previous page
 * stays on screen while the next one loads, so paging, searching and filtering never flash the grid
 * empty.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { SALES_CRM_QUERY_KEYS } from '@/features/sales/constants/prospects-query-keys.constants'
import { getClients } from '@/features/sales/services/sales-clients.service'

import type { ClientListFilters } from '@/features/sales/types/sales-client.types'

/**
 * @description Fetches one page of clients matching the filters.
 * @param {ClientListFilters} filters - Search, state, owner, status, sort, page and page size.
 * @returns {ReturnType<typeof useQuery>} The query (data is the page's rows and paging meta).
 */
export function useClients(filters: ClientListFilters) {
  return useQuery({
    queryKey: SALES_CRM_QUERY_KEYS.clients(filters),
    queryFn: () => getClients(filters),
    placeholderData: keepPreviousData,
  })
}
